import LogRocket from 'logrocket';

const APP_ID = process.env.REACT_APP_LOGROCKET_APP_ID;

// Hosts we never record. Local dev sessions would otherwise burn session quota
// and fill the dashboard with noise from our own machines.
const LOCAL_HOSTS = ['localhost', '0.0.0.0', '::1'];

const isLocalHost = () => {
  // location.hostname wraps IPv6 literals in brackets ("[::1]") and preserves a
  // fully-qualified trailing dot ("localhost.", "app.local."), either of which
  // would defeat every match below. Normalise both away before testing.
  const host = window.location.hostname
    .toLowerCase()
    .replace(/^\[|\]$/g, '')
    .replace(/\.$/, '');
  return (
    LOCAL_HOSTS.includes(host) ||
    host.endsWith('.local') ||
    // the whole 127/8 loopback block, not just 127.0.0.1
    /^127\./.test(host) ||
    // private LAN ranges, so a phone hitting the dev box over wifi is skipped too
    /^10\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host) ||
    // IPv6 unique-local (fc00::/7) and link-local (fe80::/10)
    /^f[cd][0-9a-f]{0,2}:/.test(host) ||
    /^fe[89ab][0-9a-f]?:/.test(host)
  );
};

// REACT_APP_LOGROCKET_ENABLED lets us force recording on (to smoke-test the
// integration from a dev box) or off (kill switch) without a code change.
const shouldRecord = () => {
  // No app id configured (e.g. a dev checkout without it in .env) means no
  // recording at all, rather than a runtime error from LogRocket.init.
  if (!APP_ID) return false;
  const flag = process.env.REACT_APP_LOGROCKET_ENABLED;
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  return !isLocalHost();
};

let started = false;

export const initLogRocket = () => {
  if (started || !shouldRecord()) return;
  started = true;

  LogRocket.init(APP_ID, {
    dom: {
      // Resumes and candidate contact details are on screen throughout the app.
      // Text is masked by default; opt individual elements back in with
      // data-public="true" where we actually need to read them in a replay.
      textSanitizer: true,
      inputSanitizer: true,
    },
    network: {
      requestSanitizer: (request) => {
        if (request.headers) {
          delete request.headers.Authorization;
          delete request.headers.authorization;
        }
        // Login/signup bodies carry plaintext passwords.
        if (/\/(login|users)\/?$/.test(request.url || '')) {
          request.body = undefined;
        }
        return request;
      },
      responseSanitizer: (response) => {
        // Token-bearing responses must not land in a replay. Scan the whole body:
        // truncating the scan meant a token past the cut-off kept the full body.
        if (response.body) {
          const serialized =
            typeof response.body === 'string' ? response.body : JSON.stringify(response.body);
          if (serialized && /token/i.test(serialized)) {
            response.body = undefined;
          }
        }
        return response;
      },
    },
  });
};

// Keys here mirror the login response in app/blueprints/users.py (LoginView),
// which is camelCase for the user fields and snake_case for the rest.
export const identifyLogRocket = (user) => {
  if (!started || !user) return;
  const id = user.userId || user.email;
  if (!id) return;

  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');

  LogRocket.identify(String(id), {
    email: user.email,
    ...(name ? { name } : {}),
    role: user.role || 'member',
    companyId: user.company_id,
    isSuperAdmin: Boolean(user.is_super_admin),
  });
};

export default LogRocket;
